// packages/components/src/doctor-ai/PatientDossier.tsx
import React from 'react';
import { TabbedComponent } from '@dopomogai/ui';
import { PatientHeader } from './PatientHeader';
import { OverviewTab } from './OverviewTab';
import { PatientVitals } from './PatientVitals';
import { MedicalSummaryTab } from './MedicalSummaryTab';
import { TimelineTab } from './TimelineTab';
import { DocumentsTab } from './DocumentsTab';
import type { Patient, PatientDocument } from '@dopomogai/types';
import { Clipboard, LayoutGrid, CalendarClock, FileArchive } from 'lucide-react';

interface PatientDossierProps {
  patient: Patient | null; 
  onFileAdd: (files: File[]) => void;
  onFileRemove: (fileId: string) => void;
}

// Define the shape DocumentsTab expects, for clarity
interface DocumentForTab {
  id: string;
  status: PatientDocument['status'];
  file: {
    name: string;
    size: number;
  };
}

export const PatientDossier: React.FC<PatientDossierProps> = ({ patient, onFileAdd, onFileRemove }) => {
  if (!patient) {
    return null;
  }

  // --- FIX: Transform the data to match the new 'Document' interface ---
  // This acts as an adapter between the old data shape and the new component props.
  const documentsForTab: DocumentForTab[] = patient.chart.documents.map(doc => ({
    id: doc.id,
    status: doc.status,
    // Create the nested 'file' object that DocumentsTab now requires
    file: {
      name: doc.name,
      // The old PatientDocument type might not have a size, so we provide a default
      size: (doc as any).size || 0,
    }
  }));

  const tabs = [
    {
      id: 'overview',
      label: 'AI Overview',
      icon: <LayoutGrid />,
      content: <OverviewTab chatHistory={patient.chart.chatHistory} />,
    },
    {
      id: 'summary',
      label: 'Medical Summary',
      icon: <Clipboard />,
      content: <MedicalSummaryTab patient={patient} />,
    },
    {
      id: 'timeline',
      label: 'Timeline',
      icon: <CalendarClock />,
      content: <TimelineTab chatHistory={patient.chart.chatHistory} />,
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: <FileArchive />,
      content: (
        <DocumentsTab
          // Pass the correctly-shaped data
          documents={documentsForTab as any}
          onFileAdd={onFileAdd}
          onFileRemove={onFileRemove}
          // Provide dummy props for the new RAG controls to satisfy the interface
          selectedFileIds={new Set()}
          onFileSelectionChange={() => {}}
        />
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col bg-slate-50 border-l-2 border-black">
      <PatientHeader patient={patient} />
      <div className="flex-grow overflow-y-auto p-4 md:p-6">
        <PatientVitals patient={patient} />
        <TabbedComponent tabs={tabs} />
      </div>
    </div>
  );
};